import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { Appointment, AppointmentStatus, TimeSlot } from '../models/appointment.model';
import { MOCK_APPOINTMENTS } from '../mock/mock-data';
import { AvailabilityService } from './availability.service';
import { NotificationService } from './notification.service';

/**
 * AppointmentService – Mock implementation (in-memory store).
 * TODO: Replace with Firestore collection or REST API calls.
 * Public API is identical – components won't need changes.
 */
@Injectable({ providedIn: 'root' })
export class AppointmentService {
  private _appointments$ = new BehaviorSubject<Appointment[]>(
    JSON.parse(JSON.stringify(MOCK_APPOINTMENTS)) // deep clone mock data
  );

  constructor(
    private availSvc: AvailabilityService,
    private notifSvc: NotificationService
  ) {}

  /** Stream of all appointments */
  appointments$: Observable<Appointment[]> = this._appointments$.asObservable();

  // ── Queries ──────────────────────────────────────────────────────────────────

  /** All appointments for a specific professional */
  byProfessional$(professionalId: string): Observable<Appointment[]> {
    return this.appointments$.pipe(
      map(list => list.filter(a => a.professionalId === professionalId))
    );
  }

  /** All appointments for a specific client */
  byClient$(clientId: string): Observable<Appointment[]> {
    return this.appointments$.pipe(
      map(list => list.filter(a => a.clientId === clientId))
    );
  }

  /** Single appointment by id */
  getById(id: string): Appointment | undefined {
    return this._appointments$.value.find(a => a.id === id);
  }

  /** Synchronous snapshot of all appointments */
  getAllSnapshot(): Appointment[] {
    return this._appointments$.value;
  }

  /**
   * Time slots for a professional on a given date.
   * Marks slots as unavailable if an appointment already exists at that time.
   */
  slotsForDate(professionalId: string, date: string): TimeSlot[] {
    const booked = this._appointments$.value
      .filter(a =>
        a.professionalId === professionalId &&
        a.date === date &&
        a.status !== 'cancelled'
      )
      .map(a => a.time);

    const hours = this.availSvc.getSlotsForDate(date);
    return hours.map(time => ({
      time,
      available: !booked.includes(time),
    }));
  }

  // ── Mutations ─────────────────────────────────────────────────────────────────

  /**
   * Create a new appointment.
   * TODO: replace body with Firestore addDoc() / POST request.
   */
  create(data: Omit<Appointment, 'id' | 'createdAt' | 'status'>): Appointment {
    const appointment: Appointment = {
      ...data,
      id: 'apt-' + Date.now(),
      status: 'pending',
      createdAt: new Date().toISOString(),
    };
    this._appointments$.next([...this._appointments$.value, appointment]);
    this.notifSvc.success('¡Turno reservado! Te esperamos el ' + data.date + ' a las ' + data.time + '.');
    return appointment;
  }

  /**
   * Update status of an existing appointment.
   * TODO: replace with Firestore updateDoc() / PATCH request.
   */
  updateStatus(id: string, status: AppointmentStatus): void {
    const list = this._appointments$.value.map(a =>
      a.id === id ? { ...a, status } : a
    );
    this._appointments$.next(list);
  }

  /**
   * Cancel an appointment.
   */
  cancel(id: string): void {
    this.updateStatus(id, 'cancelled');
    this.notifSvc.warning('Tu turno fue cancelado.');
  }

  /**
   * Confirm a pending appointment (professional action).
   */
  confirm(id: string): void {
    this.updateStatus(id, 'confirmed');
    this.notifSvc.success('Turno confirmado. El cliente recibirá una notificación.');
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  /** Returns next N available dates (skipping Sundays) */
  getAvailableDates(count = 30): string[] {
    const dates: string[] = [];
    const d = new Date();
    d.setDate(d.getDate() + 1);
    while (dates.length < count) {
      if (d.getDay() !== 0) { // skip Sundays
        dates.push(d.toISOString().split('T')[0]);
      }
      d.setDate(d.getDate() + 1);
    }
    return dates;
  }
}
